import React, { useEffect, useState } from 'react';
import { Download, FileText, History, X } from 'lucide-react';
import { estimateApi, EstimateMeta } from './api';
import { proposalApi, ProposalMeta } from './proposalApi';
import { fmtDate } from './utils';
import './saved-document-history.css';

interface Props {
  projectId: string;
  showEstimates: boolean;
  showProposals: boolean;
  onClose: () => void;
  presentation?: 'dialog' | 'workspace';
  onReturnToWork?: (proposalId: string) => void;
}

/** Read-only escape hatch for historical artifacts created before strict
 * stage gates. The actual Estimate/Pricing/Proposal workspaces stay locked. */
export default function LegacyDocuments({ projectId, showEstimates, showProposals, onClose, presentation = 'dialog', onReturnToWork }: Props) {
  const [estimates, setEstimates] = useState<EstimateMeta[]>([]);
  const [proposals, setProposals] = useState<ProposalMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(''); setEstimates([]); setProposals([]);
    void Promise.allSettled([
      showEstimates ? estimateApi.listEstimates(projectId) : Promise.resolve({ estimates: [] }),
      showProposals ? proposalApi.listProposals(projectId) : Promise.resolve({ proposals: [] }),
    ]).then(([estimateResult, proposalResult]) => {
      if (!live) return;
      if (estimateResult.status === 'fulfilled') setEstimates(estimateResult.value.estimates || []);
      if (proposalResult.status === 'fulfilled') setProposals(proposalResult.value.proposals || []);
      if (estimateResult.status === 'rejected' || proposalResult.status === 'rejected') setError('Some historical documents could not be loaded. Try opening this history again.');
      setLoading(false);
    });
    return () => { live = false; };
  }, [projectId, showEstimates, showProposals]);

  useEffect(() => {
    if (presentation !== 'dialog') return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, presentation]);

  const download = async (kind: 'estimate' | 'proposal', id: string, fallback: string) => {
    setDownloading(id);
    setError('');
    try {
      const response = kind === 'estimate' ? await estimateApi.estimateDocxBlob(id) : await proposalApi.proposalDocxBlob(id);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fallback + '.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (reason: any) {
      setError(reason?.message || 'The document could not be downloaded.');
    } finally {
      setDownloading(null);
    }
  };

  const inline = presentation === 'workspace';
  const title = inline ? (showProposals ? 'Saved proposals' : 'Saved estimates') : 'Historical documents';
  const content = <section className={inline ? 'saved-document-history web' : 'arch-legacy-card'} role={inline ? 'region' : 'dialog'} aria-modal={inline ? undefined : true} aria-label={title}>
      <header className={inline ? 'saved-document-history-head' : 'arch-legacy-head'}>
        <div><span className={inline ? 'saved-document-history-label' : 'arch-legacy-eyebrow'}>{inline ? <History size={15} aria-hidden="true" /> : null}Read-only history</span><h2>{title}</h2></div>
        {!inline ? <button type="button" className="arch-btn arch-btn-ghost" aria-label="Close historical documents" onClick={onClose}><X size={18} /></button> : null}
      </header>
      <p className={inline ? 'saved-document-history-hint' : 'arch-legacy-description'}>View and download saved documents. The current approval flow stays unchanged.</p>
      <div className="arch-legacy-list">
        {loading ? <p role="status">Loading documents…</p> : null}
        {!loading && showEstimates ? <section><h3>Estimates</h3>{estimates.length ? estimates.map(item => <article className="arch-legacy-row" key={item.id}>
          <FileText size={18} aria-hidden="true" /><div><strong>{item.title || 'Estimate'}</strong><small>{item.status} · {fmtDate(item.created_at)}</small></div>
          {item.has_docx ? <button type="button" className="arch-btn arch-btn-outline" disabled={downloading !== null} onClick={() => void download('estimate', item.id, item.title || 'Estimate')}><Download size={15} /> {downloading === item.id ? 'Preparing…' : 'Download DOCX'}</button> : null}
        </article>) : <p>No stored estimates.</p>}</section> : null}
        {!loading && showProposals ? <section><h3>Proposals</h3>{proposals.length ? proposals.map(item => <article className="arch-legacy-row" key={item.id}>
          <FileText size={18} aria-hidden="true" /><div><strong>{item.project || item.client || 'Proposal'}</strong><small>{item.status} · {fmtDate(item.created_at)}</small>{item.recovery ? <small>Returned by {item.recovery.restored_by_name} · {fmtDate(item.recovery.restored_at)}</small> : null}</div>
          {onReturnToWork ? <button type="button" className="arch-btn arch-btn-outline" onClick={() => onReturnToWork(item.id)}>Return to work</button> : null}
          {item.has_docx ? <button type="button" className="arch-btn arch-btn-outline" disabled={downloading !== null} onClick={() => void download('proposal', item.id, item.project || 'Proposal')}><Download size={15} /> {downloading === item.id ? 'Preparing…' : 'Download DOCX'}</button> : null}
        </article>) : <p>No stored proposals.</p>}</section> : null}
      </div>
      {error ? <p className="arch-legacy-error" role="alert">{error}</p> : null}
    </section>;
  return inline ? content : <div className="arch-legacy-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>{content}</div>;
}
